import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Injects admin-managed custom HTML (tracking pixels, widgets) into the page.
 * Managed from the admin "קוד HTML" tab (platform_settings keys).
 */
const injectInto = (target: HTMLElement, html: string, marker: string) => {
  target.querySelectorAll(`[data-custom-code="${marker}"]`).forEach((el) => el.remove());
  if (!html.trim()) return;

  const container = document.createElement("div");
  container.innerHTML = html;

  Array.from(container.childNodes).forEach((node) => {
    if (node.nodeName === "SCRIPT") {
      const original = node as HTMLScriptElement;
      const script = document.createElement("script");
      Array.from(original.attributes).forEach((a) => script.setAttribute(a.name, a.value));
      script.text = original.text;
      script.setAttribute("data-custom-code", marker);
      target.appendChild(script);
    } else {
      const el = node as HTMLElement;
      if (el.setAttribute) el.setAttribute("data-custom-code", marker);
      target.appendChild(el);
    }
  });
};

const CustomCodeInjector = () => {
  const { data } = useQuery({
    queryKey: ["custom-code-public"],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data } = await supabase
        .from("platform_settings")
        .select("key, value")
        .in("key", ["custom_head_html", "custom_body_html"]);
      const map: Record<string, string> = {};
      (data || []).forEach((row: any) => {
        map[row.key] = typeof row.value === "string" ? row.value : String(row.value ?? "");
      });
      return map;
    },
  });

  useEffect(() => {
    if (!data) return;
    injectInto(document.head, data.custom_head_html || "", "head");
    injectInto(document.body, data.custom_body_html || "", "body");
  }, [data]);

  return null;
};

export default CustomCodeInjector;
